// lib
import { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { can, CAPABILITY } from '@aytm/app/stats/page/data-quality-report/lib/tiers'
import { fieldingComplete } from '@aytm/app/stats/page/data-quality-report/lib/survey-status'
import { targetChecked } from '@aytm/app/stats/page/sidebar/data-quality/vector-check'

// components
import { Button } from 'primereact/button'

import Popup, { PopupCloseButton } from '@aytm/lib/rfd-ui/components/basic/popup'

import DataCentrifugeVector from '@aytm/app/stats/page/sidebar/data-quality/DataCentrifugeVector'

// styles
import style from '@aytm/app/stats/page/sidebar/data-quality/styles/DataCentrifugeSection.module.scss'

const DataCentrifugeSection = ({ root, tier, runEnabled = true, model }) => {
  // Vector selection and re-run are admin-only; below T0 the list is a read-only account of the
  // automated run (see CAPABILITY.SELECT_VECTORS).
  const interactive = can(tier, CAPABILITY.SELECT_VECTORS)
  const {
    total,
    vectors,
    statuses,
    scoresUIDs,
    quarantineUIDs,
    quarantineUIDsFiltered,
    quarantineCount,
    surveyStatus,
    dcLastRunTs,
    notAnalyzedCount,
    dcQuarantineEnabled,
    onCheck,
    onRangeCheck,
    onSelect,
    onRestore,
    onFetch
  } = model.dataCentrifuge

  const [error, setError] = useState(null)
  const [confirm, setConfirm] = useState(false)
  const [restoring, setRestoring] = useState(false)

  const vectorIdRef = useRef(null)
  const itemIdRef = useRef(null)
  // Target `checked` value at the moment the anchor was established. Used by Shift-range
  // instead of reading `vectors[anchorVI].checked` live — protects against RE-RUN /
  // "ALL VECTORS" / filter changes flipping the anchor's state out from under the user.
  const anchorCheckedRef = useRef(null)

  // Starting a run is administrative, so only a viewer who can start one is asked to — the same
  // pair of gates the per-vector RUN AGAIN affordance carries.
  const canRun = interactive && runEnabled

  // A vector toggle recomputes the quarantined set, so a viewer who can toggle is told the figure
  // their own selection produces. Every tier below reads a run that cannot move under them, and the
  // producer's aggregate is the figure narrowed to what that tier may see -- so it is the one that
  // agrees with the vector rows beneath it and with the report's own Data Centrifuge account. The
  // uid set is not a fallback here: it counts the whole run by response status, which is the figure
  // this sentence is deliberately not carrying below that tier. The Export label resolves an absent
  // aggregate the other way because it has a file to match rather than a panel.
  const dcQuarantineCount = can(tier, CAPABILITY.SELECT_VECTORS) ? quarantineUIDs.length : (quarantineCount ?? 0)

  const dataCentrifugeMessage = useMemo(() => {
    // The run keeps scoring while the survey fields, so its account is provisional until fielding
    // closes and the sentence says so -- the wording the report's own section subtitle already uses.
    const fielding = !fieldingComplete(surveyStatus)
    const soFar = fielding ? ' so far. Numbers can change until fielding completes' : ''
    const questionable = n => `${n} questionable ${pluralize(n, 'response', 'responses')}${soFar}`
    const rerun = `There ${pluralize(notAnalyzedCount, 'is', 'are')} ${notAnalyzedCount} not-analyzed ${pluralize(
      notAnalyzedCount,
      'response',
      'responses'
    )}. Please rerun Data Centrifuge.`

    // Whether a run is on record is all this sentence needs, and the analysed count answers it at
    // the administrative tier alone — it is a property of the run and is withheld below it, so
    // every client-tier reader resolved as never-run however many runs had completed. The last-run
    // timestamp answers the same question at every tier, and is the marker the report's own Data
    // Centrifuge subtitle already reads, so the two surfaces cannot disagree on one survey.
    return dcLastRunTs
      ? 0 < notAnalyzedCount
        ? 0 < dcQuarantineCount
          ? `During aytm proprietary Data Centrifuge process we have identified and quarantined ${questionable(
              dcQuarantineCount
            )}. ${rerun}`
          : `During aytm proprietary Data Centrifuge process we have identified ${questionable(
              scoresUIDs.length
            )}. ${rerun}`
        : 0 < dcQuarantineCount
          ? `During aytm proprietary Data Centrifuge process we have identified and quarantined ${questionable(
              dcQuarantineCount
            )}.`
          : `During aytm proprietary Data Centrifuge process we have identified ${questionable(scoresUIDs.length)}.`
      : canRun
        ? dcQuarantineEnabled
          ? 'Please run Data Centrifuge to automatically quarantine questionable and low-quality responses from the survey.'
          : 'Please run Data Centrifuge to identify questionable and low-quality responses from the survey.'
        : 'No Data Centrifuge analysis is on record for this survey yet.'
  }, [scoresUIDs.length, dcQuarantineCount, dcLastRunTs, notAnalyzedCount, dcQuarantineEnabled, canRun, surveyStatus])

  const onChange = useCallback(
    (event, vector, item) => {
      if (event.shiftKey && vectorIdRef.current) {
        const anchorVI = vectors.findIndex(v => v.id == vectorIdRef.current)
        const clickVI = vectors.findIndex(v => v.id == vector.id)

        const anchorGroup = vectors[anchorVI].group
        const anchorII = itemIdRef.current && anchorGroup ? anchorGroup.findIndex(v => v.id == itemIdRef.current) : -1

        const clickGroup = vectors[clickVI].group
        const clickII = item && clickGroup ? clickGroup.findIndex(v => v.id == item.id) : -1

        const checked = anchorCheckedRef.current

        // Bidirectional range: walk between anchor (exclusive) and click (inclusive),
        // treating anchor/click as endpoints regardless of which is higher.
        const ids = []

        if (anchorVI == clickVI) {
          if (0 <= anchorII && 0 <= clickII && anchorII != clickII) {
            const lo = anchorII < clickII ? anchorII + 1 : clickII
            const hi = anchorII < clickII ? clickII + 1 : anchorII
            anchorGroup
              .slice(lo, hi)
              .filter(v => !v.disabled)
              .map(v => ids.push(v.id))
          } else if (0 > anchorII && 0 <= clickII) {
            anchorGroup
              .slice(0, clickII + 1)
              .filter(v => !v.disabled)
              .map(v => ids.push(v.id))
          } else if (0 <= anchorII && 0 > clickII) {
            anchorGroup
              .slice(0, anchorII + 1)
              .filter(v => !v.disabled)
              .map(v => ids.push(v.id))
          }
        } else if (anchorVI < clickVI) {
          if (0 <= anchorII) {
            anchorGroup
              .slice(anchorII + 1)
              .filter(v => !v.disabled)
              .map(v => ids.push(v.id))
          }
          for (let i = anchorVI + 1; i < clickVI; i += 1) {
            if (vectors[i].disabled) continue
            ids.push(vectors[i].id)
            if (Object.hasOwn(vectors[i], 'group')) {
              vectors[i].group.filter(v => !v.disabled).map(v => ids.push(v.id))
            }
          }
          if (0 <= clickII) {
            clickGroup
              .slice(0, clickII + 1)
              .filter(v => !v.disabled)
              .map(v => ids.push(v.id))
          } else {
            ids.push(vector.id)
            if (clickGroup) clickGroup.filter(v => !v.disabled).map(v => ids.push(v.id))
          }
        } else {
          if (0 <= clickII) {
            clickGroup
              .slice(clickII)
              .filter(v => !v.disabled)
              .map(v => ids.push(v.id))
          } else {
            ids.push(vector.id)
            if (clickGroup) clickGroup.filter(v => !v.disabled).map(v => ids.push(v.id))
          }
          for (let i = clickVI + 1; i < anchorVI; i += 1) {
            if (vectors[i].disabled) continue
            ids.push(vectors[i].id)
            if (Object.hasOwn(vectors[i], 'group')) {
              vectors[i].group.filter(v => !v.disabled).map(v => ids.push(v.id))
            }
          }
          if (0 <= anchorII) {
            // Anchor-exclusive, matching the forward and same-parent branches — the anchor
            // already carries the target state, so it is never part of the range.
            anchorGroup
              .slice(0, anchorII)
              .filter(v => !v.disabled)
              .map(v => ids.push(v.id))
          }
        }

        onRangeCheck(ids, checked)

        vectorIdRef.current = vector.id
        itemIdRef.current = item ? item.id : null
        // New anchor inherits the range's target state — the next Shift-click
        // continues in the same direction.
        anchorCheckedRef.current = checked

        return true
      }

      // Plain click (toggle) or Ctrl/⌘ click (solo). Snapshot the target `checked` the upstream
      // dispatch will produce: toggle → targetChecked (which, for an expandable parent, is "any
      // child still off" rather than !vector.checked), solo → true. Deriving it any other way
      // lets the anchor disagree with what was actually applied, and the invalidation effect
      // below then silently drops the anchor — the next Shift-click would select nothing.
      vectorIdRef.current = vector.id
      itemIdRef.current = item ? item.id : null
      anchorCheckedRef.current = event.ctrlKey || event.metaKey ? true : targetChecked(vector, item, tier)

      return false
    },
    [tier, vectors, onRangeCheck]
  )

  const onError = useCallback((error = null) => setError(error), [])

  const onRestoreClick = useCallback(async () => {
    setConfirm(false)
    setRestoring(true)

    onError()
    onRestore(quarantineUIDsFiltered, error => {
      if (error) onError(error)
      setRestoring(false)
    })
  }, [quarantineUIDsFiltered, onError, onRestore])

  useEffect(() => {
    if (!vectorIdRef.current) return

    const anchor = vectors.find(v => v.id == vectorIdRef.current)
    const current =
      !anchor || anchor.disabled
        ? null
        : itemIdRef.current
          ? anchor.group?.filter(v => !v.disabled).find(v => v.id == itemIdRef.current)?.checked
          : anchor.checked

    if (current == null || current !== anchorCheckedRef.current) {
      vectorIdRef.current = null
      itemIdRef.current = null
      anchorCheckedRef.current = null
    }
  }, [vectors])

  useEffect(() => setConfirm(false), [quarantineUIDsFiltered])

  return (
    <div data-testid="stat-data-quality-panel-dc-section" className={style.section}>
      <div data-testid="stat-data-quality-panel-dc-section-title" className={style['section-title']}>
        Data Centrifuge
      </div>

      <div
        data-testid="stat-data-quality-panel-dc-message"
        className={`${style.row} ${style.note}`}
        dangerouslySetInnerHTML={{ __html: dataCentrifugeMessage }}
      />

      <div data-testid="stat-data-quality-panel-dc-vectors" className={style.vectors}>
        {/* The "ALL VECTORS" pseudo-row toggles every vector at once via onSelect('all'). It
              goes inert when every visible vector is already inert — including the empty-vectors
              case, since [].every(...) is true — because there's nothing left to "select all of." */}
        {(() => {
          const allRowInactive = (vectors || []).every(v => v.disabled) || !interactive
          return (
            <div
              data-testid="stat-data-quality-panel-dc-all-vectors"
              className={classNames(style.row, style.vector, { [style['vector-active']]: !allRowInactive })}
              onClick={allRowInactive ? null : () => onSelect('all')}
            >
              <div className={`${style['vector-header']} p-col`}>
                <span
                  data-testid="stat-data-quality-panel-dc-all-vectors-label"
                  className={classNames(style['vector-button'], { 'p-disabled': allRowInactive })}
                >
                  ALL VECTORS
                </span>
              </div>
              <div
                data-testid="stat-data-quality-panel-dc-all-vectors-total"
                className={classNames(style['vector-total'], 'p-right', { 'p-disabled': allRowInactive })}
              >
                {total}
              </div>
            </div>
          )
        })()}

        {vectors.map(vector => (
          <DataCentrifugeVector
            key={`vector-${vector.id}`}
            root={root}
            tier={tier}
            vector={vector}
            statuses={statuses}
            interactive={interactive}
            runEnabled={runEnabled}
            onCheck={onCheck}
            onSelect={onSelect}
            onChange={onChange}
            onFetch={onFetch}
          />
        ))}
      </div>

      {(0 < quarantineUIDs.length || restoring) && can(tier, CAPABILITY.RELEASE_QUARANTINE) ? (
        <div className={`${style.row} ${style['row-buttons']} p-fluid`}>
          {null !== error ? (
            <Popup
              className={`${style['component-popup']} rfd-ui-popup-simple`}
              width="250px"
              trigger="none"
              direction="topleft"
              show
              content={() => (
                <>
                  <PopupCloseButton className={style['component-popup-close']} onClose={onError}>
                    <i data-testid="stat-data-quality-panel-dc-error-close" className="icon-aytmfd-x-v2" />
                  </PopupCloseButton>
                  <span data-testid="stat-data-quality-panel-dc-error-message">{error.message}</span>
                </>
              )}
            >
              <div className={style['component-popup-anchor']} />
            </Popup>
          ) : null}
          {confirm ? (
            <>
              <Button
                data-testid="stat-data-quality-panel-dc-restore-cancel"
                className="p-button-rounded p-button-stat p-button-secondary"
                label="Cancel"
                onClick={() => setConfirm(false)}
              />
              <Button
                data-testid="stat-data-quality-panel-dc-restore-submit"
                className="p-button-rounded p-button-stat"
                label="Submit"
                onClick={onRestoreClick}
              />
            </>
          ) : (
            <Button
              data-testid="stat-data-quality-panel-dc-restore"
              className="p-button-rounded p-button-stat"
              label={
                restoring ? 'Releasing from quarantine' : `Release ${quarantineUIDsFiltered.length} from quarantine`
              }
              loading={restoring}
              disabled={restoring || !quarantineUIDsFiltered.length}
              onClick={restoring || !quarantineUIDsFiltered.length ? null : () => setConfirm(true)}
            />
          )}
        </div>
      ) : null}
    </div>
  )
}

DataCentrifugeSection.propTypes = {
  root: PropTypes.any,
  tier: PropTypes.string,
  runEnabled: PropTypes.bool,
  model: PropTypes.object
}

DataCentrifugeSection.displayName = 'DataCentrifugeSection'

export default DataCentrifugeSection
